import { useAppSSE } from 'contexts/AppSSEContext';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  addAlarmFromEvent,
  applyAlarmUpdatesFromEvent,
  removeAlarmFromEvent,
} from 'services/Alarms/alarms.service';

/**
 * Reads the signed-in workplace from the `workplaceId` cookie that
 * `services/auth/auth.service.js` writes at login AND at every token refresh.
 *
 * The cookie holds a number, or it is absent. `rememberWorkplaceId` removes it
 * rather than write anything else, so there is no unreadable value to expect
 * here.
 *
 * The backend also scopes the alarm stream to the connection's own workplace
 * now, so this is a second line of defence rather than the only one. `null`
 * means "unknown" and the handlers then keep everything rather than guess.
 *
 * @returns {number|null} The workplace id, or `null` when it is absent or not a
 *   number.
 */
const readWorkplaceId = () => {
  const raw = Cookies.get('workplaceId');
  if (raw == null || raw === '') return null;
  const workplaceId = Number(raw);
  return Number.isFinite(workplaceId) ? workplaceId : null;
};

export function useLiveAlarmUpdates(onAlarmMessage, { enabled = true } = {}) {
  const lastAlarmRefreshRef = useRef(0);
  const { subscribe } = useAppSSE();

  const throttledAlarmRefresh = useCallback(
    (eventData, eventType) => {
      const now = Date.now();

      // Protect against duplicate event emissions for a single backend update.
      if (now - lastAlarmRefreshRef.current < 750) {
        return;
      }

      lastAlarmRefreshRef.current = now;

      if (typeof onAlarmMessage === 'function') {
        onAlarmMessage(eventData, eventType);
      }
    },
    [onAlarmMessage],
  );

  useEffect(() => {
    if (!enabled) return;

    const unsubs = [
      subscribe('alarm-add', (eventData) => throttledAlarmRefresh(eventData, 'alarm-add')),
      subscribe('alarm-update', (eventData) => throttledAlarmRefresh(eventData, 'alarm-update')),
    ];

    return () => unsubs.forEach((unsub) => unsub());
  }, [enabled, subscribe, throttledAlarmRefresh]);
}

/**
 * Subscribes to alarm SSE events and keeps the `getAlarms` RTK-Query cache in
 * sync with real-time changes.
 *
 * Registers handlers for `alarm-add`, `alarm-update`, and `alarm-remove` that
 * dispatch the corresponding cache updaters from `alarms.service`, scoped to the
 * workplace this browser is signed in to. Subscriptions are torn down on unmount
 * or when `enabled` becomes false. Mount once near the app root (see
 * `SSEEventWatcher`).
 *
 * @param {Object} [options] - Hook options.
 * @param {boolean} [options.enabled=true] - When false, no subscriptions are
 *   created (e.g. while the user is unauthenticated).
 * @returns {void}
 */
export function useAlarmEvents({ enabled = true } = {}) {
  const dispatch = useDispatch();
  const { subscribe } = useAppSSE();

  useEffect(() => {
    if (!enabled) return;

    // Resolved once per effect run: the cookie cannot change without a new
    // sign-in, which remounts the tree above this hook.
    const options = { workplaceId: readWorkplaceId() };

    const handlers = {
      'alarm-add': (eventData) => addAlarmFromEvent(dispatch, eventData, options),
      'alarm-update': (eventData) => applyAlarmUpdatesFromEvent(dispatch, eventData, options),
      'alarm-remove': (eventData) => removeAlarmFromEvent(dispatch, eventData, options),
    };

    const unsubs = Object.entries(handlers).map(([eventType, handler]) =>
      subscribe(eventType, handler),
    );

    return () => unsubs.forEach((unsub) => unsub());
  }, [dispatch, subscribe, enabled]);
}
